import { RunElement } from '../../shared/run-element.js';
import { resolveSkinFromAncestors } from '../../shared/resolve-skin-name.js';
import * as defaultSkin from './skins/tabs.default.js';
import * as wireframeSkin from './skins/tabs.wireframe.js';

export const TABS_ORIENTATIONS = Object.freeze(['horizontal', 'vertical']);
export const TABS_ACTIVATION_MODES = Object.freeze(['auto', 'manual']);

const _defaultSheet = new CSSStyleSheet();
_defaultSheet.replaceSync(defaultSkin.styles);

const _wireframeSheet = new CSSStyleSheet();
_wireframeSheet.replaceSync(wireframeSkin.styles || '');

class RunTabs extends RunElement {
  #active = null;
  #orientation = 'horizontal';
  #activation = 'auto';
  #scrollable = false;
  #skin = null;
  #rendering = false;
  #tabs = [];
  #panels = [];
  #uid = Math.random().toString(36).slice(2, 8);
  #noAutoActivate = false;

  static get observedAttributes() {
    return ['active', 'orientation', 'activation', 'scrollable', 'skin'];
  }

  _onKeydownBound = (e) => this.#handleKeydown(e);
  _onClickBound = (e) => this.#handleClick(e);
  _onTabCloseBound = (e) => this.#handleTabClose(e);
  _onSlotChangeBound = (e) => this.#handleSlotChange(e);

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this._onKeydownBound);
    this.addEventListener('click', this._onClickBound);
    this.addEventListener('tab-close', this._onTabCloseBound);
    this.#injectVtStyles();
    this.#updateAria();
    this.requestUpdate();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this._onKeydownBound);
    this.removeEventListener('click', this._onClickBound);
    this.removeEventListener('tab-close', this._onTabCloseBound);
    if (this.shadowRoot) {
      this.shadowRoot.querySelectorAll('slot').forEach(slot => {
        slot.removeEventListener('slotchange', this._onSlotChangeBound);
      });
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'active':      this.active = newValue; break;
      case 'orientation': this.orientation = newValue; break;
      case 'activation':  this.activation = newValue; break;
      case 'scrollable':  this.scrollable = newValue !== null; break;
      case 'skin':        this.skin = newValue; break;
    }
  }

  get active() { return this.#active; }
  set active(value) {
    // Treat empty string the same as null — re-resolves from first tab
    const coerced = (value === null || value === undefined || value === '') ? null : String(value).trim();
    if (this.#active === coerced) return;
    this.#active = coerced;
    if (coerced !== null) {
      this.setAttribute('active', coerced);
    } else {
      this.removeAttribute('active');
    }
    this.#updateAria();
    this.requestUpdate();
  }

  get orientation() { return this.#orientation; }
  set orientation(value) {
    const coerced = TABS_ORIENTATIONS.includes(value) ? value : 'horizontal';
    if (this.#orientation === coerced) return;
    this.#orientation = coerced;
    this.setAttribute('orientation', coerced);
    this.#updateAria();
    this.requestUpdate();
  }

  get activation() { return this.#activation; }
  set activation(value) {
    const coerced = TABS_ACTIVATION_MODES.includes(value) ? value : 'auto';
    if (this.#activation === coerced) return;
    this.#activation = coerced;
    this.setAttribute('activation', coerced);
    this.requestUpdate();
  }

  get scrollable() { return this.#scrollable; }
  set scrollable(value) {
    const coerced = Boolean(value);
    if (this.#scrollable === coerced) return;
    this.#scrollable = coerced;
    this.toggleAttribute('scrollable', coerced);
    this.requestUpdate();
  }

  get skin() { return this.#skin; }
  set skin(value) {
    const coerced = (value === null || value === undefined) ? null : String(value);
    if (this.#skin === coerced) return;
    this.#skin = coerced;
    if (coerced !== null) {
      this.setAttribute('skin', coerced);
    } else {
      this.removeAttribute('skin');
    }
    this.requestUpdate();
  }

  show(name) {
    if (!name) return false;
    const tab = this.#tabs.find(t => this.#tabName(t) === name);
    if (!tab || tab.disabled || tab.hasAttribute('disabled')) return false;
    this.#activateByName(name);
    return true;
  }

  activateTab(name) {
    return this.show(name);
  }

  render() {
    return undefined;
  }

  updated(changedProperties) {
    super.updated?.(changedProperties);
    if (this.#rendering) return;
    this.#rendering = true;
    try {
      const resolvedSkin = this.#resolveSkin();
      if (resolvedSkin === 'wireframe') {
        this.shadowRoot.adoptedStyleSheets = [_wireframeSheet];
        wireframeSkin.render(this);
      } else {
        this.shadowRoot.adoptedStyleSheets = [_defaultSheet];
        defaultSkin.render(this);
      }
      this.shadowRoot.querySelectorAll('slot').forEach(slot => {
        slot.removeEventListener('slotchange', this._onSlotChangeBound);
        slot.addEventListener('slotchange', this._onSlotChangeBound);
      });
    } finally {
      this.#rendering = false;
    }
    this.#updateAria();
    this.#syncChildren();
  }

  #resolveSkin() {
    if (this.#skin) return this.#skin;
    return resolveSkinFromAncestors(this);
  }

  #tabName(tab) {
    return tab.getAttribute('panel') || tab.panel || null;
  }

  #panelName(panel) {
    return panel.getAttribute('name') || panel.name || null;
  }

  #findPanel(name) {
    return this.#panels.find(p => this.#panelName(p) === name) || null;
  }

  #collectChildren() {
    // Read light-DOM children directly. The skin uses named slots
    // (slot="tablist" / slot="panel"), assigned in #assignSlots, so a
    // default-slot read would miss children once they have been reassigned.
    const assigned = Array.from(this.children);
    this.#tabs = assigned.filter(el => el.localName === 'run-tab');
    this.#panels = assigned.filter(el => el.localName === 'run-tab-panel');
  }

  // Route each child to the correct named slot so the skin lays the tablist
  // out as a bar and the panels into the panel region. Consumers place plain
  // <run-tab>/<run-tab-panel> children; run-tabs owns the slot wiring.
  #assignSlots() {
    this.#tabs.forEach(tab => {
      if (tab.getAttribute('slot') !== 'tablist') tab.setAttribute('slot', 'tablist');
    });
    this.#panels.forEach(panel => {
      if (panel.getAttribute('slot') !== 'panel') panel.setAttribute('slot', 'panel');
    });
  }

  #ensureIds() {
    this.#tabs.forEach((tab, i) => {
      if (!tab.id) tab.id = `run-tab-${this.#uid}-${i}`;
    });
    this.#panels.forEach((panel, i) => {
      // Prefer the panel's name as its id. run-tab sets aria-controls to the
      // panel NAME (its documented contract); making the panel's id equal its
      // name keeps that reference valid. Falling back to a generated id only
      // when the panel is unnamed avoids the dangling-aria-controls bug where
      // run-tab pointed at the name while the panel carried a uid id.
      if (!panel.id) {
        const name = this.#panelName(panel);
        panel.id = name || `run-tab-panel-${this.#uid}-${i}`;
      }
    });
  }

  #linkTabsAndPanels() {
    this.#tabs.forEach(tab => {
      const name = this.#tabName(tab);
      const panel = this.#panels.find(p => this.#panelName(p) === name);
      if (panel) {
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('aria-labelledby', tab.id);
      }
    });
  }

  #syncChildren() {
    this.#collectChildren();
    if (this.#tabs.length === 0) return;

    this.#assignSlots();
    this.#ensureIds();
    this.#linkTabsAndPanels();

    // Default active to first non-disabled tab that has a matching panel
    if (!this.#noAutoActivate && this.#active === null) {
      const first = this.#tabs.find(t => {
        if (t.disabled || t.hasAttribute('disabled')) return false;
        const name = this.#tabName(t);
        return name !== null && this.#findPanel(name) !== null;
      });
      if (first) {
        const name = this.#tabName(first);
        if (name) {
          this.#active = name;
          this.setAttribute('active', name);
        }
      }
    }

    // Apply roles to slotted children that may not self-manage role
    this.#tabs.forEach(tab => {
      if (!tab.hasAttribute('role')) tab.setAttribute('role', 'tab');
    });
    this.#panels.forEach(panel => {
      if (!panel.hasAttribute('role')) panel.setAttribute('role', 'tabpanel');
    });

    // Apply aria-selected and tabindex to all tabs
    this.#tabs.forEach(tab => {
      const isActive = this.#tabName(tab) === this.#active;
      const isDisabled = tab.disabled || tab.hasAttribute('disabled');
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive && !isDisabled ? '0' : '-1');
      // run-tab drives its visual active state (the indicator underline) off
      // its `active` property — set it so the selected tab highlights. `selected`
      // is also set for any tab implementation that exposes it.
      if ('active' in tab) tab.active = isActive;
      if ('selected' in tab) tab.selected = isActive;
    });

    // Apply hidden to panels
    this.#panels.forEach(panel => {
      const isActive = this.#panelName(panel) === this.#active;
      if (isActive) {
        panel.removeAttribute('hidden');
        if ('active' in panel) panel.active = true;
      } else {
        panel.setAttribute('hidden', '');
        if ('active' in panel) panel.active = false;
      }
    });
  }

  #injectVtStyles() {
    if (document.getElementById('run-tabs-vt-styles')) return;
    const style = document.createElement('style');
    style.id = 'run-tabs-vt-styles';
    style.textContent = [
      '::view-transition-old(root),',
      '::view-transition-new(root) {',
      '  animation: none;',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  #startTransition(mutationFn) {
    if (!document.startViewTransition || this.reducedMotion) {
      mutationFn();
      return;
    }
    document.startViewTransition(() => mutationFn());
  }

  #updateAria() {
    // aria-orientation lives on the tablist element rendered by the skin in shadow DOM
    const tablist = this.shadowRoot && this.shadowRoot.querySelector('[role="tablist"]');
    if (tablist) {
      tablist.setAttribute('aria-orientation', this.#orientation);
    }
  }

  #handleSlotChange(event) {
    const prevTabCount = this.#tabs.length;
    this.#syncChildren();
    // If tabs appeared where there were none, re-enable auto-activation for new content
    if (prevTabCount === 0 && this.#tabs.length > 0) {
      this.#noAutoActivate = false;
      this.#syncChildren();
    }
    const slot = event.target;
    const assigned = slot.assignedElements({ flatten: true });
    const slotLabel = slot.name || 'default';
    this.toggleAttribute(`data-has-${slotLabel}`, assigned.length > 0);
  }

  #handleKeydown(event) {
    const path = event.composedPath();
    const target = path.find(el => el && el.localName === 'run-tab');
    if (!target) return;

    const enabledTabs = this.#tabs.filter(t => !t.disabled && !t.hasAttribute('disabled'));
    if (enabledTabs.length === 0) return;

    const currentEnabledIndex = enabledTabs.indexOf(target);
    const isHorizontal = this.#orientation === 'horizontal';
    const prevKey = isHorizontal ? 'ArrowLeft' : 'ArrowUp';
    const nextKey = isHorizontal ? 'ArrowRight' : 'ArrowDown';

    let newTab = null;

    switch (event.key) {
      case nextKey: {
        event.preventDefault();
        newTab = enabledTabs[(currentEnabledIndex + 1) % enabledTabs.length];
        break;
      }
      case prevKey: {
        event.preventDefault();
        newTab = enabledTabs[(currentEnabledIndex - 1 + enabledTabs.length) % enabledTabs.length];
        break;
      }
      case 'Home': {
        event.preventDefault();
        newTab = enabledTabs[0];
        break;
      }
      case 'End': {
        event.preventDefault();
        newTab = enabledTabs[enabledTabs.length - 1];
        break;
      }
      case 'Enter':
      case ' ': {
        if (this.#activation === 'manual') {
          event.preventDefault();
          const name = this.#tabName(target);
          if (name) this.#activateByName(name);
        }
        return;
      }
      default:
        return;
    }

    if (newTab) {
      // Move focus: exactly one tab has tabindex=0
      this.#tabs.forEach(t => t.setAttribute('tabindex', '-1'));
      newTab.setAttribute('tabindex', '0');
      newTab.focus();

      if (this.#activation === 'auto') {
        const name = this.#tabName(newTab);
        if (name) this.#activateByName(name);
      }
    }
  }

  #handleClick(event) {
    const path = event.composedPath();
    const target = path.find(el => el && el.localName === 'run-tab');
    if (!target) return;
    if (target.disabled || target.hasAttribute('disabled')) return;
    const name = this.#tabName(target);
    if (name) this.#activateByName(name);
  }

  #handleTabClose(event) {
    event.stopPropagation();
    const closedTab = event.target;
    const closedName = this.#tabName(closedTab);
    const closedIndex = this.#tabs.indexOf(closedTab);

    if (closedName !== this.#active) return;

    // Prefer right/below sibling; fallback left/above; null when last
    let nextTab = null;
    for (let i = closedIndex + 1; i < this.#tabs.length; i++) {
      const t = this.#tabs[i];
      if (!t.disabled && !t.hasAttribute('disabled') && t !== closedTab) {
        nextTab = t;
        break;
      }
    }
    if (!nextTab) {
      for (let i = closedIndex - 1; i >= 0; i--) {
        const t = this.#tabs[i];
        if (!t.disabled && !t.hasAttribute('disabled') && t !== closedTab) {
          nextTab = t;
          break;
        }
      }
    }

    if (nextTab) {
      const nextName = this.#tabName(nextTab);
      if (nextName) this.#activateByName(nextName);
    } else {
      const prev = this.#active;
      this.#active = null;
      this.removeAttribute('active');
      if (prev !== null) {
        const prevPanel = this.#findPanel(prev);
        this.#emitTabHide(prev, prevPanel);
      }
      this.#noAutoActivate = true;
      this.#syncChildren();
      this.requestUpdate();
    }
  }

  #activateByName(name) {
    if (name === this.#active) return;
    const prev = this.#active;
    this.#active = name;
    if (name !== null) {
      this.setAttribute('active', name);
    } else {
      this.removeAttribute('active');
    }
    // hide fires BEFORE sync (previously-active panel still visible at event time)
    if (prev !== null) {
      const prevPanel = this.#findPanel(prev);
      this.#emitTabHide(prev, prevPanel);
    }
    // sync first so new panel is visible when show fires; wrap in VT for crossfade
    this.#startTransition(() => this.#syncChildren());
    if (name !== null) {
      const newPanel = this.#findPanel(name);
      this.#emitTabShow(name, newPanel);
    }
    this.requestUpdate();
  }

  #emitTabShow(name, panel) {
    this.dispatchEvent(new CustomEvent('run-tab-show', {
      bubbles: true,
      composed: true,
      detail: { name, panel },
    }));
  }

  #emitTabHide(name, panel) {
    this.dispatchEvent(new CustomEvent('run-tab-hide', {
      bubbles: true,
      composed: true,
      detail: { name, panel },
    }));
  }
}

if (!customElements.get('run-tabs')) {
  customElements.define('run-tabs', RunTabs);
}

export { RunTabs };
export default RunTabs;
